import { createContext, useContext, ReactNode, useEffect, useState, useMemo } from 'react';
import { useQuery, ApolloClient } from '@apollo/client';
import { GET_BRANDS_QUERY } from '../queries/getBrandsQuery';
import { Brand } from '../types/Brand';
import { transformBrandResponse } from '../tools/transformBrandResponse';
import { GetBrandListingQuery } from '../schemas/generated-types/graphql';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

type BrandById = Record<string, Brand>;
type BrandByKey = Record<string, Brand>;

interface BrandContextValue {
    brands: Brand[];
    brandById: BrandById;
    brandByKey: BrandByKey;
    loading: boolean;
    error: any;
    initialize: () => void;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

interface BrandsProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const BrandsProvider = ({ children, apolloClient }: BrandsProviderProps) => {
    const [brandById, setBrandById] = useState<BrandById>({});
    const [brandByKey, setBrandByKey] = useState<BrandByKey>({});

    const [initialized, setInitialized] = useState(false);

    const query = GET_BRANDS_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery<GetBrandListingQuery>(query, {
        client: apolloClient,
        errorPolicy: 'all', // Allow partial data instead of full rejection
        skip: !initialized, // Do not run query until initialization is triggered.
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, initialized, queryError });

    const initialize = () => {
        if (!initialized) {
            setInitialized(true);
        }
    };

    // choose data source: real query or fallback
    const brands = useMemo(() => {
        const source = queryData ?? fallbackData;
        if (!source) {
            return [];
        }
        return transformBrandResponse(source);
    }, [queryData, fallbackData]);

    useEffect(() => {
        const brandById: BrandById = {};
        const brandByKey: BrandByKey = {};

        brands.forEach((brand) => {
            brandById[brand.id] = brand;
            brandByKey[brand.key] = brand;
        });

        setBrandById(brandById);
        setBrandByKey(brandByKey);
    }, [brands]);

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <BrandContext.Provider
            value={{
                brands,
                brandById,
                brandByKey,
                loading,
                error,
                initialize,
            }}
        >
            {children}
        </BrandContext.Provider>
    );
};

// Unsafe hook: does not trigger initialization.
function useBrandsUnsafe() {
    const context = useContext(BrandContext);
    if (!context) {
        throw new Error('useBrands must be used within a BrandsProvider');
    }
    return context;
}

// Safe hook: triggers lazy initialization when a consumer uses it.
function useBrands() {
    const context = useBrandsUnsafe();
    // Trigger lazy initialization when a consumer uses this hook.
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export { BrandsProvider, useBrands, useBrandsUnsafe };
export type { BrandContextValue, BrandById, BrandByKey };
